import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { usePageTitle } from '../contexts/PageTitleContext';
import {
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';

interface ActivityLog {
  id: string;
  user_id: string;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
  changes: Array<{
    field: string;
    old_value: any;
    new_value: any;
  }>;
  details: Record<string, any>;
  ip_address: string;
  user_agent: string;
  status: string;
  error_message: string;
  timestamp: string;
  company_id: string;
}

const UserActivity: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [limit] = useState(100);
  const [offset] = useState(0);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Fetch activity logs for this user
  const { data: activityData, isLoading, error } = useQuery({
    queryKey: ['user-activity-logs', id, limit, offset],
    queryFn: () => apiService.getUserActivityLogs(id!, limit, offset),
    enabled: !!id,
  });

  // Fetch user details for the page title
  const { data: userData } = useQuery({
    queryKey: ['user', id],
    queryFn: () => apiService.getUserById(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (userData) {
      setPageTitle('Activity History', `View activity logs for ${userData.name || userData.email}`);
    } else {
      setPageTitle('Activity History', 'View activity logs for this user');
    }
  }, [userData, setPageTitle]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatAction = (action: string) => {
    // Convert action names to readable format
    return action
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const formatFieldName = (field: string) => {
    return field
      .split('.')
      .map(part => part.replace(/_/g, ' '))
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' → ');
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'None';
    }
    if (typeof value === 'boolean') {
      return value ? 'Enabled' : 'Disabled';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const toggleItem = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allIds = new Set<string>(logs.map((log: ActivityLog) => log.id));
    setExpandedItems(allIds);
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-governate-600 dark:border-governate-400 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading activity history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <XCircleIcon className="mx-auto h-12 w-12 text-red-500 dark:text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Error loading activity logs</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Please try again later.</p>
        <button
          onClick={() => navigate('/users')}
          className="mt-4 px-4 py-2 bg-governate-600 text-white rounded-lg hover:bg-governate-700 transition-colors"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const logs = activityData?.logs || [];

  return (
    <div className="p-6 space-y-6">
      {/* Activity logs */}
      <div className="bg-white dark:bg-black rounded-lg shadow overflow-hidden">
        {logs.length === 0 ? (
          <div className="text-center py-12 px-6">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No activity yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Activity logs for this user will appear here.
            </p>
          </div>
        ) : (
          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Recent Activity ({logs.length})
              </h2>
              <div className="flex space-x-2">
                <button
                  onClick={expandAll}
                  className="px-3 py-1.5 text-xs font-medium text-governate-600 dark:text-governate-400 hover:text-governate-700 dark:hover:text-governate-300 hover:bg-governate-50 dark:hover:bg-governate-900/30 rounded-lg transition-colors"
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Collapse All
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {logs.map((log: ActivityLog) => {
                const isExpanded = expandedItems.has(log.id);
                
                return (
                  <div
                    key={log.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden transition-all"
                  >
                    {/* Accordion Header - Always visible */}
                    <button
                      onClick={() => toggleItem(log.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={`p-2 rounded-lg ${
                          log.status === 'success' 
                            ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' 
                            : log.status === 'failure'
                            ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {log.status === 'success' ? (
                            <CheckCircleIcon className="w-4 h-4" />
                          ) : log.status === 'failure' ? (
                            <XCircleIcon className="w-4 h-4" />
                          ) : (
                            <ClockIcon className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {formatAction(log.action)}
                          </h3>
                          <div className="flex items-center space-x-4 mt-1">
                            {log.resource_type && log.resource_name && (
                              <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                                <span>{log.resource_type}: {log.resource_name}</span>
                              </div>
                            )}
                            <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                              <CalendarIcon className="w-3 h-3" />
                              <span>{formatDate(log.timestamp)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          log.status === 'success'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : log.status === 'failure'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {log.status}
                        </span>
                        {isExpanded ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        )}
                      </div>
                    </button>

                    {/* Accordion Content - Only visible when expanded */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
                        {/* Resource Information */}
                        {(log.resource_type || log.resource_id) && (
                          <div className="mt-3">
                            <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Resource:</h4>
                            <div className="text-xs bg-white dark:bg-black rounded p-3 border border-gray-200 dark:border-gray-700 space-y-1">
                              {log.resource_type && (
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>{' '}
                                  <span className="text-gray-600 dark:text-gray-400">{log.resource_type}</span>
                                </div>
                              )}
                              {log.resource_id && (
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">ID:</span>{' '}
                                  <span className="text-gray-600 dark:text-gray-400 font-mono text-[10px]">{log.resource_id}</span>
                                </div>
                              )}
                              {log.resource_name && (
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span>{' '}
                                  <span className="text-gray-600 dark:text-gray-400">{log.resource_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Changes */}
                        {log.changes && log.changes.length > 0 && (
                          <div className="mt-3">
                            <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Changes:</h4>
                            <div className="space-y-2">
                              {log.changes.map((change, idx) => (
                                <div key={idx} className="text-xs bg-white dark:bg-black rounded p-3 border border-gray-200 dark:border-gray-700">
                                  <div className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {formatFieldName(change.field)}
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-red-600 dark:text-red-400 line-through">
                                      {formatValue(change.old_value)}
                                    </span>
                                    <span className="text-gray-400 dark:text-gray-500">→</span>
                                    <span className="text-green-600 dark:text-green-400 font-medium">
                                      {formatValue(change.new_value)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Error message */}
                        {log.error_message && (
                          <div className="mt-3">
                            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-3 border border-red-200 dark:border-red-800">
                              <span className="font-medium">Error:</span> {log.error_message}
                            </div>
                          </div>
                        )}

                        {/* Additional details */}
                        {log.ip_address && (
                          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium">IP Address:</span> {log.ip_address}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserActivity;

